import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { NotificationService } from "@/lib/notifications/notification.service";
import type { NotificationPreferences } from "@/lib/auth/auth.types";
import { useAuth } from "@/lib/contexts/auth.context";

const PREFERENCES_QUERY_KEY = ["notification-preferences"] as const;

export default function SettingsPage() {
	const { t } = useTranslation("common");
	const queryClient = useQueryClient();
	const { refreshUserProfile } = useAuth();

	const { data: preferences, isLoading } = useQuery({
		queryKey: PREFERENCES_QUERY_KEY,
		queryFn: () => NotificationService.getPreferences(),
	});

	const mutation = useMutation({
		mutationFn: (update: Partial<NotificationPreferences>) =>
			NotificationService.updatePreferences(update),
		onMutate: async (update) => {
			await queryClient.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY });
			const previous = queryClient.getQueryData<NotificationPreferences>(
				PREFERENCES_QUERY_KEY,
			);
			if (previous) {
				queryClient.setQueryData<NotificationPreferences>(
					PREFERENCES_QUERY_KEY,
					{ ...previous, ...update },
				);
			}
			return { previous };
		},
		onError: (_error, _update, context) => {
			if (context?.previous) {
				queryClient.setQueryData(PREFERENCES_QUERY_KEY, context.previous);
			}
			toast.error(t("settings.emailNotifications.error"));
		},
		onSuccess: (data) => {
			queryClient.setQueryData(PREFERENCES_QUERY_KEY, data);
			toast.success(t("settings.emailNotifications.saved"));
			// Keep the auth context's user object in sync with the new preferences.
			void refreshUserProfile();
		},
	});

	const handleToggle = (key: keyof NotificationPreferences, value: boolean) => {
		mutation.mutate({ [key]: value });
	};

	return (
		<div className="mx-auto w-full max-w-2xl px-4 py-8 md:py-12">
			<h1 className="mb-6 text-2xl font-bold text-foreground">
				{t("settings.title")}
			</h1>

			<Card>
				<CardHeader>
					<div className="flex items-center gap-2">
						<Bell className="h-5 w-5 text-primary" />
						<CardTitle>{t("settings.emailNotifications.title")}</CardTitle>
					</div>
					<CardDescription>
						{t("settings.emailNotifications.description")}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-1">
					<PreferenceRow
						title={t("settings.emailNotifications.engagementTitle")}
						description={t("settings.emailNotifications.engagementDescription")}
						checked={preferences?.engagementEmailsEnabled ?? true}
						disabled={isLoading || mutation.isPending}
						onCheckedChange={(value) =>
							handleToggle("engagementEmailsEnabled", value)
						}
					/>

					<Separator className="my-1" />

					<PreferenceRow
						title={t("settings.emailNotifications.marketingTitle")}
						description={t("settings.emailNotifications.marketingDescription")}
						checked={preferences?.marketingEmailsEnabled ?? true}
						disabled={isLoading || mutation.isPending}
						onCheckedChange={(value) =>
							handleToggle("marketingEmailsEnabled", value)
						}
					/>

					<p className="pt-4 text-xs text-muted-foreground">
						{t("settings.emailNotifications.transactionalNote")}
					</p>
				</CardContent>
			</Card>
		</div>
	);
}

interface PreferenceRowProps {
	title: string;
	description: string;
	checked: boolean;
	disabled?: boolean;
	onCheckedChange: (value: boolean) => void;
}

function PreferenceRow({
	title,
	description,
	checked,
	disabled,
	onCheckedChange,
}: PreferenceRowProps) {
	return (
		<div className="flex items-start justify-between gap-4 py-3">
			<div className="min-w-0">
				<p className="text-sm font-medium text-foreground">{title}</p>
				<p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
			</div>
			<Switch
				checked={checked}
				disabled={disabled}
				onCheckedChange={onCheckedChange}
				className="mt-1 shrink-0"
			/>
		</div>
	);
}
